import type { AnnouncementDto, AnnouncementType } from '@mesterplan/api-client';

export const ANNOUNCEMENT_TYPE_LABEL: Record<AnnouncementType, string> = {
  feature: 'Ny funktion',
  improvement: 'Forbedring',
  fix: 'Rettelse',
};

const MONTHS_LONG = [
  'januar', 'februar', 'marts', 'april', 'maj', 'juni',
  'juli', 'august', 'september', 'oktober', 'november', 'december',
];
const MONTHS_SHORT = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

/** `publishedAt` is stored as UTC midnight, so read the UTC calendar day. */
function utcParts(iso: string): { day: number; month: number; year: number } | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return { day: d.getUTCDate(), month: d.getUTCMonth(), year: d.getUTCFullYear() };
}

/** "26. oktober 2026" */
export function formatReleaseDate(iso: string): string {
  const p = utcParts(iso);
  return p ? `${p.day}. ${MONTHS_LONG[p.month]} ${p.year}` : '';
}

/** "26. okt" */
export function formatShortDate(iso: string): string {
  const p = utcParts(iso);
  return p ? `${p.day}. ${MONTHS_SHORT[p.month]}` : '';
}

/** Platform tags on a note: "Web" and "App 1.1.0". */
export function platformTags(item: Pick<AnnouncementDto, 'platforms' | 'minAppVersion'>): string[] {
  const tags: string[] = [];
  if (item.platforms.includes('web')) tags.push('Web');
  if (item.platforms.includes('ios') || item.platforms.includes('android')) {
    tags.push(item.minAppVersion ? `App ${item.minAppVersion}` : 'App');
  }
  return tags;
}

export function newsCountLabel(count: number): string {
  return `${count} ${count === 1 ? 'nyhed' : 'nyheder'}`;
}

export interface ReleaseGroup {
  releaseKey: string;
  heading: string;
  items: AnnouncementDto[];
}

/** Groups a newest-first list by release, keeping order. Heading = the release's date. */
export function groupByRelease(items: AnnouncementDto[]): ReleaseGroup[] {
  const groups: ReleaseGroup[] = [];
  for (const item of items) {
    const last = groups[groups.length - 1];
    if (last && last.releaseKey === item.releaseKey) {
      last.items.push(item);
    } else {
      groups.push({ releaseKey: item.releaseKey, heading: formatReleaseDate(item.publishedAt), items: [item] });
    }
  }
  return groups;
}

/** `https://` actions open in a new tab; everything else is a react-router path. */
export function isExternalAction(url: string): boolean {
  return /^https?:\/\//i.test(url);
}
